import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { countries, getFlagUrl, findCountryByValue } from './countries';
import type { Country } from './countries';

export interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  required?: boolean;
}

export function CountrySelect({
  value,
  onChange,
  placeholder = 'Select country',
  disabled = false,
  error = false,
  className = '',
  required = false,
}: CountrySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedCountry = value ? findCountryByValue(value) : null;

  const filteredCountries = searchQuery.trim()
    ? countries.filter((c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.dialCode.includes(searchQuery) ||
        c.code.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : countries;

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      setSearchQuery('');
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (country: Country) => {
    onChange(country.name);
    setIsOpen(false);
  };

  const baseClass = `flex items-center w-full h-10 px-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 ${
    error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'
  } ${className}`;

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] flex flex-col"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: Math.max(dropdownPosition.width, 220),
        maxHeight: 300,
      }}
    >
      <div className="p-2 border-b border-gray-100 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search country..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>
      <div className="overflow-y-auto flex-1 p-1">
        {filteredCountries.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-4">No countries found</p>
        ) : (
          filteredCountries.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => handleSelect(country)}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded hover:bg-gray-100 transition-colors ${
                selectedCountry?.code === country.code ? 'bg-emerald-50' : ''
              }`}
            >
              <img
                src={getFlagUrl(country.code)}
                alt={country.name}
                className="w-6 h-4 object-cover rounded flex-shrink-0"
              />
              <span className="flex-1 text-left text-sm text-gray-700">{country.name}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) updatePosition();
          }
        }}
        disabled={disabled}
        className={`${baseClass} text-left justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center space-x-2 min-w-0">
          {selectedCountry ? (
            <>
              <img
                src={getFlagUrl(selectedCountry.code)}
                alt={selectedCountry.name}
                className="w-5 h-4 object-cover rounded flex-shrink-0"
              />
              <span className="text-sm text-gray-700 truncate">{selectedCountry.name}</span>
            </>
          ) : (
            <span className="text-sm text-gray-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}
